// src/services/adminSettings.js
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase-config';

const SETTINGS_DOC_ID = 'adminSettings';

/**
 * Get admin settings from Firestore
 * @returns {Promise<Object>} Admin settings object
 */
export const getAdminSettings = async () => {
  try {
    const settingsDoc = await getDoc(doc(db, 'settings', SETTINGS_DOC_ID));
    if (settingsDoc.exists()) {
      return settingsDoc.data();
    }
    // Return default settings if document doesn't exist
    return getDefaultSettings();
  } catch (error) {
    console.error('Error fetching admin settings:', error);
    return getDefaultSettings();
  }
};

/**
 * Subscribe to admin settings changes
 * @param {Function} callback - Callback function that receives settings updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAdminSettings = (callback) => {
  const settingsRef = doc(db, 'settings', SETTINGS_DOC_ID);
  
  return onSnapshot(settingsRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data());
    } else {
      callback(getDefaultSettings());
    }
  }, (error) => {
    console.error('Error subscribing to admin settings:', error);
    callback(getDefaultSettings());
  });
};

/**
 * Update admin settings in Firestore
 * @param {Object} updates - Partial settings object to update
 * @returns {Promise<void>}
 */
export const updateAdminSettings = async (updates) => {
  try {
    const settingsRef = doc(db, 'settings', SETTINGS_DOC_ID);
    const currentSettings = await getAdminSettings();
    
    await setDoc(settingsRef, {
      ...currentSettings,
      ...updates,
      lastUpdated: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.error('Error updating admin settings:', error);
    throw error;
  }
};

/**
 * Get default admin settings
 * @returns {Object} Default settings
 */
export const getDefaultSettings = () => {
  return {
    protectedRouteEnabled: true,
    promptLimits: {
      free: 5,
      basic: 10,
      hybrid: 50,
      pro: 50,
      enterprise: 50,
      paliGPT: Infinity,
    },
    lastUpdated: null,
  };
};

/**
 * Check if user is admin
 * Only nuralhuda.co@gmail.com has admin access
 * @param {Object} user - Firebase user object
 * @returns {boolean}
 */
export const isUserAdmin = (user) => {
  if (!user || !user.email) return false;
  
  // Only allow access to the specific admin email
  return user.email.toLowerCase() === 'nuralhuda.co@gmail.com';
};

